/**
 * OnboardingTour — 4-step guided tour shown on first Dashboard mount.
 *
 * Storage: `ghostcomm_onboarded` localStorage flag. Intentionally shares
 * the `ghostcomm_` prefix so panic-button wipe also clears it — a user
 * reinstalling after a wipe gets the tour again (they probably need it).
 *
 * Placement: slides in from the bottom-right as a 320px-wide card so it
 * never obstructs the chat surface or the panic button itself. Each
 * step has a matching spotlight animation that pulses on the relevant
 * UI element (purely decorative — we don't block interaction).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck,
  AlertTriangle,
  Fingerprint,
  KeyRound,
  X,
  ArrowRight,
  ArrowLeft,
  Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { setItem as safeSetItem } from '../utils/storage';

const FLAG = 'ghostcomm_onboarded';

const STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    eyebrow: 'Welcome to Government Shield',
    title: "You're in a room nobody else can read.",
    body: (
      <>
        Messages are encrypted on your device, <b>auto-destruct after 24h</b>,
        and keys never leave this browser. Four quick tips to get you
        moving — takes 30 seconds.
      </>
    ),
    accent: 'from-primary/30 to-primary/5',
  },
  {
    id: 'panic',
    icon: AlertTriangle,
    eyebrow: 'Step 1 · Escape hatch',
    title: 'If someone walks up, hit the panic button.',
    body: (
      <>
        The <b className="text-red-400">red shield</b> in the bottom-right
        corner wipes everything local (keys, cached messages, trust ledger)
        and logs you out instantly. You can also enable{' '}
        <b>Decoy Mode</b> in Settings — a second password that unlocks a
        plausible-looking fake account.
      </>
    ),
    accent: 'from-red-500/25 to-red-500/5',
  },
  {
    id: 'key-transparency',
    icon: Fingerprint,
    eyebrow: 'Step 2 · Verify your contacts',
    title: 'Watch the safety stripe under each name.',
    body: (
      <>
        Every 1:1 chat header has a 3-px coloured bar:{' '}
        <b className="text-slate-400">slate</b> (untrusted — click to verify),{' '}
        <b className="text-emerald-400">emerald</b> (verified),{' '}
        <b className="text-red-400">red</b> (silent key swap — possible MITM).
        Verify once, and any future impersonation is flagged before your
        next message.
      </>
    ),
    accent: 'from-emerald-500/25 to-emerald-500/5',
  },
  {
    id: 'recovery',
    icon: KeyRound,
    eyebrow: 'Step 3 · Save your recovery key',
    title: 'Lose it and the account is gone. Seriously.',
    body: (
      <>
        We showed you a <b>64-char recovery key</b> when you signed up —
        write it down, print it, or stash it in a password manager. It's
        the <i>only</i> way to reset your password. We can't recover it
        for you: the server only stores a hash. Regenerate anytime under{' '}
        <b>Settings → Privacy</b>.
      </>
    ),
    accent: 'from-amber-500/25 to-amber-500/5',
  },
  {
    id: 'hover',
    icon: ShieldCheck,
    eyebrow: 'Step 4 · The hover-to-reveal pattern',
    title: 'Messages stay blurred until you look at them.',
    body: (
      <>
        Over-the-shoulder readers see blurred pills; the message only
        decodes under your cursor. Press <kbd className="px-1.5 py-0.5 bg-slate-800 border border-border rounded text-[10px]">Tab</kbd>
        {' '}then <kbd className="px-1.5 py-0.5 bg-slate-800 border border-border rounded text-[10px]">R</kbd>{' '}
        to add a reaction. <kbd className="px-1.5 py-0.5 bg-slate-800 border border-border rounded text-[10px]">Ctrl+K</kbd>{' '}
        opens the command palette for everything else. You're ready.
      </>
    ),
    accent: 'from-sky-500/25 to-sky-500/5',
  },
];

export const OnboardingTour = () => {
  const [stepIdx, setStepIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  // Only mount if the user hasn't finished the tour. Wait a beat so the
  // Dashboard has a moment to settle — a wall of "you logged in" UI is
  // annoying. The 900ms delay is intentional, not a lint mistake.
  useEffect(() => {
    try {
      if (window.localStorage.getItem(FLAG)) return;
    } catch {
      // private-mode browsers: still show the tour
    }
    const t = window.setTimeout(() => {
      setVisible(true);
      requestAnimationFrame(() => setEntered(true));
    }, 900);
    return () => window.clearTimeout(t);
  }, []);

  const finish = () => {
    safeSetItem(FLAG, new Date().toISOString());
    setEntered(false);
    window.setTimeout(() => setVisible(false), 250);
  };

  const next = () => {
    if (stepIdx === STEPS.length - 1) finish();
    else setStepIdx((i) => i + 1);
  };
  const prev = () => setStepIdx((i) => Math.max(0, i - 1));

  const step = STEPS[stepIdx];
  const Icon = step?.icon;
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEPS.length - 1;

  const progressPct = useMemo(
    () => ((stepIdx + 1) / STEPS.length) * 100,
    [stepIdx]
  );

  if (!visible || !step) return null;

  return (
    <div
      className={`fixed bottom-5 right-5 z-50 w-[340px] max-w-[calc(100vw-2rem)] transform transition-all duration-300 ease-out ${
        entered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
      data-testid="onboarding-tour"
      data-step={step.id}
      role="dialog"
      aria-labelledby="onboarding-title"
    >
      <div
        className={`relative rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden bg-gradient-to-br ${step.accent}`}
      >
        {/* Close */}
        <button
          type="button"
          onClick={finish}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-slate-500 hover:text-slate-200 hover:bg-slate-800/60 transition-colors"
          aria-label="Skip tour"
          data-testid="onboarding-skip"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="p-5 pr-10">
          <div className="flex items-start gap-3 mb-4">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900/60 border border-border shrink-0">
              <Icon className="w-5 h-5 text-slate-100" />
            </div>
            <div className="min-w-0 pt-0.5">
              <div className="text-[10px] uppercase tracking-[2px] text-slate-500 font-semibold">
                {step.eyebrow}
              </div>
              <h3
                id="onboarding-title"
                className="text-[15px] font-semibold text-slate-100 leading-snug mt-1"
              >
                {step.title}
              </h3>
            </div>
          </div>

          <p className="text-[13px] text-slate-400 leading-relaxed">{step.body}</p>

          {/* Progress */}
          <div className="mt-4 mb-3">
            <div className="h-1 rounded-full bg-slate-800/80 overflow-hidden">
              <div
                className="h-full bg-primary transition-[width] duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="mt-1.5 text-[10px] text-slate-600 font-mono">
              {stepIdx + 1} / {STEPS.length}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={prev}
              disabled={isFirst}
              className="text-xs text-slate-400 hover:text-slate-100 disabled:opacity-30"
              data-testid="onboarding-back"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Back
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={next}
              className="gap-1.5"
              data-testid="onboarding-next"
            >
              {isLast ? "I'm ready" : 'Next'}
              {!isLast && <ArrowRight className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
